<template>
  <vs-dialog v-model="isOpen" width="550px" prevent-close @close="close">
    <template #header>
      <h4 class="dialog-title">
        <i class="fal fa-undo"></i> ກັບຄືນລາຍການ
      </h4>
    </template>

    <div class="dialog-content">
      <!-- Transaction Details -->
      <div class="tx-details">
        <div class="detail-row">
          <span class="label">ID:</span>
          <span class="value">#{{ transaction.id }}</span>
        </div>
        <div class="detail-row">
          <span class="label">ປະເພດ:</span>
          <span :class="['value', 'tx-type-badge', transaction.transaction_type.toLowerCase()]">
            {{ getTypeLabel(transaction.transaction_type) }}
          </span>
        </div>
        <div class="detail-row">
          <span class="label">ຈຳນວນ:</span>
          <span class="value amount">{{ formatCurrency(transaction.amount) }}</span>
        </div>
        <div class="detail-row" v-if="transaction.branch_name || transaction.branch_prefix">
          <span class="label">ສາຂາ:</span>
          <span class="value">{{ transaction.branch_prefix }} - {{ transaction.branch_name }}</span>
        </div>
        <div class="detail-row">
          <span class="label">ເລກອ້າງອີງ:</span>
          <span class="value mono">{{ transaction.ref_no || '-' }}</span>
        </div>
        <div class="detail-row" v-if="transaction.notes">
          <span class="label">ໝາຍເຫດ:</span>
          <span class="value">{{ transaction.notes }}</span>
        </div>
        <div class="detail-row">
          <span class="label">ວັນທີ:</span>
          <span class="value">{{ transaction.created_at }}</span>
        </div>
      </div>

      <div class="warning-box">
        <i class="fal fa-exclamation-triangle"></i>
        <span v-if="reversalDirection === 'debit'">
          ການກັບຄືນລາຍການນີ້ຈະ<strong>ຫັກເງິນ</strong>ຈາກກະເປົ໋າຂອງສາຂາ
        </span>
        <span v-else-if="reversalDirection === 'credit'">
          ການກັບຄືນລາຍການນີ້ຈະ<strong>ຄືນເງິນ</strong>ເຂົ້າກະເປົ໋າຂອງສາຂາ
        </span>
        <!-- Defence in depth: unreachable through the Reverse button, which requires
             is_reversible, and the server only sets that when the movement is non-zero. -->
        <span v-else>
          ການກັບຄືນລາຍການນີ້ຈະປັບຍອດເງິນຂອງກະເປົ໋າຂອງສາຂາ
        </span>
      </div>

      <!-- Reason -->
      <div class="form-group">
        <label class="label">ເຫດຜົນ <span class="required">*</span></label>
        <textarea
          class="textarea"
          v-model="reason"
          rows="3"
          placeholder="ລະບຸເຫດຜົນໃນການກັບຄືນ (ຢ່າງໜ້ອຍ 10 ຕົວອັກສອນ)..."
          :disabled="isSubmitting"
        ></textarea>
        <p class="help-text" v-if="reason.length > 0 && reason.length < 10">
          ຕ້ອງມີຢ່າງໜ້ອຍ 10 ຕົວອັກສອນ ({{ reason.length }}/10)
        </p>
      </div>

      <!-- Two-step confirmation -->
      <div class="form-group">
        <label class="label">ພິມ <strong>CONFIRM</strong> ເພື່ອຢືນຢັນ</label>
        <input
          class="input"
          type="text"
          v-model="confirmText"
          placeholder="CONFIRM"
          :disabled="isSubmitting"
          @keyup.enter="canSubmit && submit()"
        >
      </div>

      <!-- Error Display -->
      <div v-if="errorMsg" class="notification is-danger is-light">
        <p>{{ errorMsg }}</p>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <vs-button flat @click="close" :disabled="isSubmitting">ຍົກເລີກ</vs-button>
        <vs-button danger @click="submit" :loading="isSubmitting" :disabled="!canSubmit">
          <i class="fal fa-undo"></i> ກັບຄືນລາຍການ
        </vs-button>
      </div>
    </template>
  </vs-dialog>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatCurrency, balanceDelta } from '@/services/walletFormatters';

const ERROR_MESSAGES = {
  'insufficient_balance': 'ຍອດເງິນບໍ່ພຽງພໍ',
  'transaction.already_rolled_back': 'ລາຍການນີ້ຖືກກັບຄືນແລ້ວ',
  'transaction.not_reversible': 'ປະເພດລາຍການນີ້ບໍ່ສາມາດກັບຄືນໄດ້',
  'transaction.not_found': 'ບໍ່ພົບລາຍການ',
  'cod.deadline_exceeded': 'ເກີນກຳນົດ COD',
  'freight.deadline_exceeded': 'ເກີນກຳນົດຄ່າຂົນສົ່ງ',
};

export default {
  name: 'ReverseTransactionDialog',
  props: {
    transaction: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isOpen: true,
      reason: '',
      confirmText: '',
      isSubmitting: false,
      errorMsg: null,
    };
  },
  computed: {
    canSubmit() {
      return this.reason.trim().length >= 10 && this.confirmText === 'CONFIRM' && !this.isSubmitting;
    },
    // The reversal moves money the OPPOSITE way the row itself did, and the row's own balance
    // movement is the only field that cannot lie about which way that was: a refund-of-top-up is
    // a REFUND row that DEBITED the wallet, so the old type test stated the wrong direction on
    // it. Same rule the server uses (design D2 of wallet-reversal-sign-fixes). A row that cannot
    // say (missing or equal balances) gets the neutral wording — never a guessed direction.
    reversalDirection() {
      const delta = balanceDelta(this.transaction);
      if (delta === null || delta === 0) return null;
      return delta > 0 ? 'debit' : 'credit';
    },
  },
  methods: {
    formatCurrency,
    getTypeLabel(type) {
      const labels = {
        TOP_UP: 'ເຕີມເງິນ',
        DEDUCT: 'ຫັກເງິນ',
        REFUND: 'ຄືນເງິນ',
      };
      return labels[type] || type;
    },
    close() {
      this.isOpen = false;
      this.$emit('close');
    },
    async submit() {
      if (!this.canSubmit) return;
      this.isSubmitting = true;
      this.errorMsg = null;
      try {
        await walletService.reverseTransaction(this.$axios, this.transaction.id, this.reason.trim());
        this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'success', 3000, 'ກັບຄືນລາຍການສຳເລັດ');
        this.$emit('reversed');
        this.close();
      } catch (error) {
        const { message, messageKey } = extractErrorInfo(error);
        this.errorMsg = ERROR_MESSAGES[messageKey] || message || 'ເກີດຂໍ້ຜິດພາດ';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 18px;
  font-weight: 600;
  i { margin-right: 8px; color: #dc3545; }
}

.dialog-content {
  padding: 20px;
}

.tx-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;

    .label {
      color: #666;
      font-size: 13px;
    }
    .value {
      font-weight: 500;
      font-size: 13px;
    }
    .amount {
      font-size: 16px;
      font-weight: 700;
      color: #333;
    }
    .mono {
      font-family: monospace;
      font-size: 12px;
    }
  }
}

.tx-type-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  &.deduct { background: #f8d7da; color: #721c24; }
  &.refund { background: #d4edda; color: #155724; }
  &.top_up { background: #cce5ff; color: #004085; }
}

.warning-box {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #856404;

  i { margin-right: 8px; }
}

.form-group {
  margin-bottom: 16px;

  .label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #666;
  }

  .textarea, .input {
    width: 100%;
  }

  .help-text {
    font-size: 12px;
    color: #dc3545;
    margin-top: 4px;
  }

  .required { color: #dc3545; }
}

.notification {
  margin-top: 12px;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
